import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext/AuthContext'; 
import PostWithdrawalRequestService from './service/postWithdrawalRequest'; 
import { BankAccountCreationDTO } from './service/models'; 

const textFieldStyle = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent', 
    color: '#FFFFFF',
    borderRadius: 2, 
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)', 
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)', 
    },
    '&.Mui-focused fieldset': {
      borderColor: '#5B68F6', 
    },
  },
  '& .MuiInputLabel-root': {
    color: '#9CA3AF',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5B68F6',
  },
  '& .MuiFormHelperText-root': {
    color: '#9CA3AF',
  },
};

interface AddBankAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddBankAccountModal: React.FC<AddBankAccountModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('area');
  const { tokens } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<BankAccountCreationDTO>({
    accountLabel: '',
    accountHolderName: '',
    bankName: '',
    iban: '',
    bicSwift: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        accountLabel: '',
        accountHolderName: '',
        bankName: '',
        iban: '',
        bicSwift: '',
      });
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (
      !formData.iban ||
      !formData.bicSwift ||
      !formData.accountHolderName ||
      !formData.bankName
    ) {
      setError(t('Veuillez remplir tous les champs obligatoires'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (tokens) {
        const bankNameClean = formData.bankName.trim();
        
        const cleanPayload: BankAccountCreationDTO = {
          accountHolderName: formData.accountHolderName.trim(),
          bankName: bankNameClean,
          iban: formData.iban.replace(/\s+/g, '').toUpperCase(), 
          bicSwift: formData.bicSwift.replace(/\s+/g, '').toUpperCase(),
          accountLabel: formData.accountLabel.trim() || `Compte ${bankNameClean}`, 
        };

        await PostWithdrawalRequestService.createUserBankAccount(
          tokens.accessToken,
          cleanPayload,
        );
        
        onSuccess();
        onClose();
        
        setFormData({
          accountLabel: '',
          accountHolderName: '',
          bankName: '',
          iban: '',
          bicSwift: '',
        });
      }
    } catch (err: any) {
      console.error("Erreur ajout banque:", err);
      let errorMsg = t('Une erreur est survenue lors de la création du compte bancaire');
      if (err.response?.data) {
          const data = err.response.data;
          if (data.errors && typeof data.errors === 'object') {
              const keys = Object.keys(data.errors);
              if (keys.length > 0) {
                  errorMsg = `${keys[0]}: ${data.errors[keys[0]]}`;
              }
          } 
          else if (data.message) {
              errorMsg = data.message;
          }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#0f1116', 
          color: '#FFFFFF',
          borderRadius: 2,
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ fontSize: 24, fontWeight: 600, color: '#FFFFFF', pb: 2, pt: 3, px: 3 }}>
        {t('Ajouter un compte bancaire')}
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('Libellé (Optionnel)')}
            name="accountLabel"
            value={formData.accountLabel}
            onChange={handleChange}
            fullWidth
            placeholder={t('Ex: Ma banque principale')}
            sx={textFieldStyle}
          />
          <TextField
            label={t('Nom du titulaire')}
            name="accountHolderName"
            value={formData.accountHolderName}
            onChange={handleChange}
            required
            fullWidth
            sx={textFieldStyle}
          />
          <TextField
            label={t('Nom de la banque')}
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            required
            fullWidth
            sx={textFieldStyle}
          />
          <TextField
            label={t('IBAN')}
            name="iban"
            value={formData.iban}
            onChange={handleChange}
            required
            fullWidth
            helperText={t("Saisissez l'IBAN sans espaces (formatage auto)")}
            sx={textFieldStyle}
          />
          <TextField
            label={t('BIC/SWIFT')}
            name="bicSwift"
            value={formData.bicSwift}
            onChange={handleChange}
            required
            fullWidth
            sx={textFieldStyle}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: '#9CA3AF',
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 500,
            px: 3,
            '&:hover': { 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#FFFFFF',
            }
          }}
        >
          {t('Annuler')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          sx={{
            backgroundColor: '#354eef',
            color: '#FFFFFF',
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 1.5,
            '&:hover': { 
              backgroundColor: '#4C5CE6',
            },
            '&:disabled': {
              backgroundColor: 'rgba(91, 104, 246, 0.4)',
              color: 'rgba(255, 255, 255, 0.5)',
            }
          }}
        >
          {loading ? t('Enregistrement...') : t('Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBankAccountModal;